import {inject} from 'react-ioc'
import {ELASTIC_MAX_RESULTS} from 'constants/elasticsearch'
import {parseTextFilter} from 'lib/helpers/parseTextFilter'
import {type TableQueryResult, type TableRequestParams} from 'models/table'
import {ctx} from 'new/ctx'
import {ApiStore} from 'new/stores/ApiStore'
import {SessionStore} from 'new/stores/SessionStore'
import {type ChangePasswordData} from 'types/ChangePasswordData'
import {type InvitationPermission, type User, UserStatus} from 'types/User'

///////////////////////////////////////////////////////////////////////////////

export class UsersApiService {
  #sessionStore = inject<SessionStore>(this, SessionStore)
  #apiStore = inject<ApiStore>(this, ApiStore)

  constructor() {
    ctx.usersApiService = this
  }

  async fetchCurrentUser() {
    const {token} = this.#sessionStore.getTokenInfo()

    if (!token) {
      return undefined
    }

    const {data} = await this.#apiStore.get<any, {data: User}>('/users/myself')

    return data
  }

  async save(data: Partial<User>) {
    const {uuid, ...rest} = data

    return this.#apiStore.patch(`/users/${uuid}`, rest)
  }

  async fetchOrganisationUsers(params: TableRequestParams) {
    const {token} = this.#sessionStore.getTokenInfo()
    const request = buildRequest(params)

    if (!token) {
      return undefined
    }

    const {data} = await this.#apiStore.post<
      any,
      {data: TableQueryResult<Partial<User>>}
    >(
      `/search/users/organisation?organisation_uuid=${params.organisationUuid}`,
      request
    )

    const hits = data.hits.map(user => {
      return {
        ...user,
        status:
          user.status === UserStatus.PENDING &&
          user.invitation_expires_at &&
          Math.floor(Date.now() / 1000) > Number(user.invitation_expires_at)
            ? UserStatus.INVITE_EXPIRED
            : user.status,
      }
    })

    return {...data, hits}
  }

  async inviteUsersToOrganisation(
    organisationUuid: string,
    emails: string[],
    permissions: InvitationPermission[],
    recaptchaToken?: string
  ) {
    const data =
      permissions.length > 0
        ? {emails, permissions, recaptcha_token: recaptchaToken}
        : {emails, recaptcha_token: recaptchaToken}
    return await this.#apiStore.post(
      `/users/invite-to-organisation/${organisationUuid}`,
      data
    )
  }

  async resendInvite(data: Partial<User>, organisationUuid: string) {
    const {uuid} = data

    return this.#apiStore.post(
      `/users/${uuid}/resend-invite/${organisationUuid}`
    )
  }

  async revokeInvite(data: Partial<User>, organisationUuid: string) {
    const {uuid} = data

    return this.#apiStore.post(
      `/users/${uuid}/revoke-invite/${organisationUuid}`
    )
  }

  async changeEmail(email: string, recaptchaToken?: string) {
    const data = {email, recaptcha_token: recaptchaToken}

    return this.#apiStore.post('/users/myself/request-change-email', data)
  }

  async changePassword(data: ChangePasswordData) {
    return this.#apiStore.post('/users/myself/change-password', data)
  }
}

///////////////////////////////////////////////////////////////////////////////
// Helpers
///////////////////////////////////////////////////////////////////////////////

const buildQuery = (params: TableRequestParams) => {
  const filters: any[] = []

  const {free_text} = params.filter

  if (free_text) {
    const {query} = parseTextFilter(free_text, true)

    if (query) {
      filters.push({
        $and: [
          {
            $or: [
              {
                $query: {
                  email: query,
                },
              },
              {
                $query: {
                  first_name: query,
                },
              },
              {
                $query: {
                  last_name: query,
                },
              },
              {
                $query: {
                  username: query,
                },
              },
            ],
          },
        ],
      })
    }
  }

  return filters.length === 1 ? filters[0] : {$and: filters}
}

const buildRequest = (params: TableRequestParams) => {
  let size = params.pagination.pageSize
  const from = size * (params.pagination.current - 1)

  if (size + from > ELASTIC_MAX_RESULTS) {
    size = ELASTIC_MAX_RESULTS - from
  }

  return {
    query: buildQuery(params),
    size,
    from,
    sort:
      params.sorter.field && params.sorter.order
        ? {[params.sorter.field]: getOrder(params.sorter.order)}
        : {last_login: 'DESC'},
  }
}

////////////////////////////////////////////////////////////////////////////////

const getOrder = (antSorterOrder: string): string => {
  return antSorterOrder === 'ascend' ? 'ASC' : 'DESC'
}
